import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	Link,
	Navigate,
	Route,
	BrowserRouter as Router,
	Routes,
	useNavigate,
	useParams,
} from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { DialogProvider, useDialog } from './components/DialogProvider';
import { ErrorDialog } from './components/ErrorDialog';
import { FormLayout } from './components/FormLayout';
import { Header } from './components/Header';
import { NavigationBar } from './components/NavigationBar';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { SimplePerformanceIndicator } from './components/shared/SimplePerformanceIndicator';
import { Sidebar } from './components/Sidebar';
import { TagsAdmin } from './components/TagsAdmin';
import { Toast } from './components/Toast';
import { useWorkflowState, WorkflowEditor } from './components/WorkflowEditor';
import { WorkflowSetupDialog } from './components/WorkflowSetupDialog';
import { WorkflowSuccessDialog } from './components/WorkflowSuccessDialog';
import { AuthProvider } from './contexts/AuthContext';
import { ClipboardProvider } from './contexts/ClipboardContext';
import { MenuProvider } from './contexts/MenuContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { WorkflowProvider } from './contexts/WorkflowContext';
import { folderTreeKeys } from './hooks/useFolderTree';
import { useProductionPerformanceMonitor } from './hooks/useProductionPerformanceMonitor';
import { api } from './lib/api-client';
import { applyAutoLayout } from './lib/auto-layout';
import {
	convertConditionConfigToExpression,
	convertExpressionToConditionConfig,
	getAvailableFields,
	getFlattenedFields,
} from './lib/workflow-utils';
import { cleanupWorkflowForSave } from './lib/workflow-validator';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AuditLogsPage } from './pages/AuditLogsPage';
import { ExecutionDetail } from './pages/ExecutionDetail';
import { ExecutionList } from './pages/ExecutionList';
import { FolderManager } from './pages/FolderManager';
import { FoldersPage } from './pages/FoldersPage';
import { LoginPage } from './pages/LoginPage';
import { SecurityDashboard } from './pages/SecurityDashboard';
import { TakeFormPage } from './pages/TakeFormPage';
import { WorkflowList } from './pages/WorkflowList';
import { initializeEnterprise } from './utils/productionOptimizations';

interface WorkflowMetadata {
	id?: string;
	title: string;
	description: string;
	folderId?: string;
	tags: string[];
	createdAt: string;
	updatedAt: string;
	status?: 'draft' | 'published' | 'archived';
}

interface ApiError {
	message: string | string[];
	error?: string;
	statusCode?: number;
}

/**
 * Create a React Query client with optimized settings
 */
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
			retry: (failureCount, error: any) => {
				// Don't retry on 4xx errors except 408, 429
				if (error?.statusCode >= 400 && error?.statusCode < 500) {
					return error?.statusCode === 408 || error?.statusCode === 429;
				}
				return failureCount < 3;
			},
			refetchOnWindowFocus: false,
			refetchOnReconnect: true,
		},
		mutations: {
			retry: 1,
		},
	},
});

/**
 * Component for editing an existing workflow
 */
function EditWorkflowPage() {
	const { workflowId } = useParams<{ workflowId: string }>();
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [initialWorkflowData, setInitialWorkflowData] = useState<any>(null);

	useEffect(() => {
		const loadWorkflowForEdit = async () => {
			if (!workflowId) {
				setError('No workflow ID provided');
				setIsLoading(false);
				return;
			}

			try {
				setIsLoading(true);
				setError(null);

				// Fetch workflow from API
				const apiFlow = await api.flows.getFlow(workflowId);
				console.log('Loaded workflow for editing:', apiFlow);

				// Transform API response to editor format
				const editorData = transformApiToEditorFormat(apiFlow);
				console.log('DEBUG: Loading workflow data:', {
					apiFlowStatus: apiFlow.status,
					editorDataStatus: editorData.metadata.status,
					apiFlow: apiFlow,
				});
				setInitialWorkflowData(editorData);
			} catch (err: any) {
				console.error('Failed to load workflow for editing:', err);
				setError(err.message || 'Failed to load workflow');
			} finally {
				setIsLoading(false);
			}
		};

		loadWorkflowForEdit();
	}, [workflowId]);

	if (isLoading) {
		return (
			<div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
				<div className="text-center">
					<div className="mx-auto mb-4 w-12 h-12 rounded-full border-b-2 border-blue-600 animate-spin"></div>
					<p className="text-gray-600 dark:text-gray-400">Loading workflow...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
				<div className="text-center">
					<div className="mb-4 text-xl text-red-500">❌</div>
					<h2 className="mb-2 text-xl font-semibold text-gray-900 dark:text-white">
						Failed to Load Workflow
					</h2>
					<p className="mb-4 text-gray-600 dark:text-gray-400">{error}</p>
					<Link
						to="/flows"
						className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent shadow-sm hover:bg-blue-700"
					>
						← Back to Workflows
					</Link>
				</div>
			</div>
		);
	}

	return <WorkflowApp initialWorkflowData={initialWorkflowData} />;
}

/**
 * Transform API flow format to visual editor format
 */
const transformApiToEditorFormat = (apiFlow: any) => {
	const visualNodes: any[] = [];

	// Transform submissionForm to visual node
	if (apiFlow.submissionForm) {
		const submissionFormNode = {
			id: apiFlow.submissionForm.id,
			type: 'submission-form',
			position: apiFlow.submissionForm.position || { x: 100, y: 100 },
			data: {
				label: apiFlow.submissionForm.config.name || 'Form Trigger',
				category: 'integrations' as const,
				type: 'submission-form' as const,
				config: apiFlow.submissionForm.config,
				isSubmissionForm: true,
				formFields: apiFlow.submissionForm.config.fields || [],
			},
		};
		visualNodes.push(submissionFormNode);
	}

	// Transform DAG nodes
	if (apiFlow.dagDefinition?.nodes) {
		apiFlow.dagDefinition.nodes.forEach((node: any) => {
			let nodeConfig = node.config || {};

			// Special handling for condition nodes - convert simple expression back to complex config
			if (node.type === 'condition' && nodeConfig.expression && !nodeConfig.conditions) {
				nodeConfig = convertExpressionToConditionConfig(nodeConfig.expression);
			}

			// Special handling for start.form nodes - ensure form fields are properly loaded
			let additionalData = {};
			if (node.type === 'start.form') {
				additionalData = {
					formFields: nodeConfig.fields || [],
					isSubmissionForm: true,
				};
			}

			const visualNode = {
				id: node.id,
				type: node.type,
				position: node.position || { x: 300, y: 200 },
				data: {
					label: node.label || getNodeLabel(node.type),
					category: getNodeCategory(node.type),
					type: node.type,
					config: nodeConfig,
					isSubmissionForm: false,
					...additionalData,
				},
			};
			visualNodes.push(visualNode);
		});
	}

	return {
		nodes: visualNodes,
		edges:
			apiFlow.dagDefinition?.edges.map((edge: any) => {
				// Check if this edge is from a condition node
				const sourceNode = visualNodes.find((node: any) => node.id === edge.source);
				if (sourceNode?.type === 'condition' && edge.condition) {
					return {
						...edge,
						type: 'condition', // Use custom condition edge component
						data: {
							condition: edge.condition,
							label: edge.condition === 'true' ? 'True' : 'False',
						},
					};
				}
				return edge;
			}) || [],
		metadata: {
			id: apiFlow.id,
			title: apiFlow.name,
			description: apiFlow.description,
			folderId: apiFlow.folderId,
			tags: apiFlow.tags || [],
			createdAt: apiFlow.createdAt,
			updatedAt: apiFlow.updatedAt,
			status: apiFlow.status || 'draft',
		},
	};
};

/**
 * Get display label for node type
 */
const getNodeLabel = (nodeType: string): string => {
	const labels: Record<string, string> = {
		// Trigger nodes
		'start.form': 'Form Trigger',
		'start.webhook': 'Webhook Trigger',
		'start.schedule': 'Schedule Trigger',
		'start.slack': 'Slack Trigger',
		start: 'Start', // Legacy start node
		// Processing nodes
		'api-call': 'API Call',
		transform: 'Transform',
		condition: 'Condition',
		validator: 'Validator',
		logger: 'Logger',
		// Integrations
		email: 'Email',
		slack: 'Slack',
		database: 'Database',
		'submission-form': 'Form Trigger',
		hubspot: 'HubSpot',
		'google-sheets': 'Google Sheets',
		// Advanced nodes
		loop: 'Loop',
		wait: 'Wait',
		'webhook-listener': 'Webhook Listener',
		approval: 'Approval',
		storage: 'Storage',
		'webhook-caller': 'Webhook Caller',
		switch: 'Switch',
		retry: 'Retry',
		parallel: 'Parallel',
		// Flow control
		end: 'End',
	};
	return labels[nodeType] || nodeType;
};

/**
 * Get category for node type
 */
const getNodeCategory = (nodeType: string): string => {
	const categories: Record<string, string> = {
		// Trigger nodes
		'start.form': 'triggers',
		'start.webhook': 'triggers',
		'start.schedule': 'triggers',
		'start.slack': 'triggers',
		start: 'triggers', // Legacy start node
		// Flow control
		end: 'flow-control',
		// Processing nodes
		'api-call': 'processing',
		transform: 'processing',
		condition: 'processing',
		pdf: 'processing',
		// Integrations
		email: 'integrations',
		slack: 'integrations',
		database: 'integrations',
		'submission-form': 'integrations',
		hubspot: 'integrations',
		'google-sheets': 'integrations',
		// Advanced nodes
		validator: 'advanced',
		logger: 'advanced',
		loop: 'advanced',
		wait: 'advanced',
		'webhook-listener': 'advanced',
		approval: 'advanced',
		storage: 'advanced',
		'webhook-caller': 'advanced',
		switch: 'advanced',
		retry: 'advanced',
		parallel: 'advanced',
	};
	return categories[nodeType] || 'processing';
};

function WorkflowApp({ initialWorkflowData }: { initialWorkflowData?: any }) {
	const navigate = useNavigate();
	const { alert, confirm } = useDialog();
	const workflowState = useWorkflowState();
	const queryClient = useQueryClient();
	const [showSetupDialog, setShowSetupDialog] = useState(false);
	const [isLoading, setIsLoading] = useState(false); // Add loading state
	const [errorDialog, setErrorDialog] = useState<{
		isOpen: boolean;
		error: ApiError | null;
	}>({
		isOpen: false,
		error: null,
	});
	const [successDialog, setSuccessDialog] = useState<{
		isOpen: boolean;
		workflowId: string;
		workflowName: string;
		isPublished: boolean;
	}>({
		isOpen: false,
		workflowId: '',
		workflowName: '',
		isPublished: false,
	});

	const [toast, setToast] = useState<{
		isVisible: boolean;
		message: string;
		type: 'success' | 'error' | 'info';
	}>({
		isVisible: false,
		message: '',
		type: 'success',
	});

	// Store React Flow instance from WorkflowEditor
	const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);

	const [workflowMetadata, setWorkflowMetadata] = useState<WorkflowMetadata>(() => {
		// For new workflows (no initialWorkflowData), clear any existing metadata
		if (!initialWorkflowData) {
			localStorage.removeItem('workflow-metadata');
			localStorage.removeItem('workflow-setup-seen');
			return {
				title: undefined,
				description: '',
				folderId: undefined,
				tags: [],
				createdAt: new Date().toISOString(),
				updatedAt: new Date().toISOString(),
				status: 'draft',
			};
		}

		// For existing workflows, use the API data directly and don't load from localStorage
		// This ensures the status from the API is always used
		if (initialWorkflowData.metadata) {
			return initialWorkflowData.metadata;
		}

		// Fallback for edge cases
		return {
			title: undefined,
			description: '',
			folderId: undefined,
			tags: [],
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
			status: 'draft',
		};
	});

	// Helper function to get current workflow state from React Flow
	const getCurrentWorkflowState = () => {
		// Use React Flow instance if available, otherwise fall back to workflow state
		// This prevents issues during initialization and ensures we get the most current data
		if (reactFlowInstance) {
			try {
				const nodes = reactFlowInstance.getNodes();
				const edges = reactFlowInstance.getEdges();

				// Validate that we got valid data
				if (Array.isArray(nodes) && Array.isArray(edges)) {
					return { nodes, edges };
				} else {
					console.warn(
						'React Flow instance returned invalid data, falling back to workflow state'
					);
				}
			} catch (error) {
				console.error('Error getting data from React Flow instance:', error);
			}
		}

		// Fall back to workflow state if React Flow instance is not available or returns invalid data
		return { nodes: workflowState.nodes, edges: workflowState.edges };
	};

	// Use initialWorkflowData if provided for editing existing workflows
	useEffect(() => {
		if (initialWorkflowData) {
			setIsLoading(true); // Set loading state

			// Load the workflow data into the editor - API data takes precedence over localStorage
			setWorkflowMetadata(initialWorkflowData.metadata);

			if (reactFlowInstance) {
				reactFlowInstance.setNodes(initialWorkflowData.nodes);
				reactFlowInstance.setEdges(initialWorkflowData.edges);

				// Apply auto-layout and fit view after a short delay to ensure nodes are rendered
				setTimeout(() => {
					applyAutoLayout(reactFlowInstance, 'TB', true);
					setIsLoading(false); // Clear loading state after layout
				}, 100);
			} else {
				// If React Flow instance is not ready yet, clear loading after a delay
				setTimeout(() => {
					setIsLoading(false);
				}, 500);
			}
		}
	}, [initialWorkflowData, reactFlowInstance]);

	// Save metadata to localStorage whenever it changes (but not for existing workflows during initial load)
	useEffect(() => {
		// Only save to localStorage for new workflows or after the initial load is complete
		if (!initialWorkflowData || !isLoading) {
			localStorage.setItem('workflow-metadata', JSON.stringify(workflowMetadata));
		}
	}, [workflowMetadata, initialWorkflowData, isLoading]);

	// Ensure setup dialog shows for new workflows
	useEffect(() => {
		// For new workflows (no initialWorkflowData), always show setup dialog
		if (!initialWorkflowData) {
			setShowSetupDialog(true);
		}
	}, [initialWorkflowData]);

	// Check if workflow is ready for saving (has proper title and submission form)
	const isWorkflowReadyForSave = () => {
		const { nodes } = getCurrentWorkflowState();
		const hasProperTitle =
			workflowMetadata.title &&
			workflowMetadata.title.trim() !== '' &&
			workflowMetadata.title !== 'Untitled Workflow';

		// Check if workflow has a submission form (new requirement)
		const hasSubmissionForm = nodes.some(
			(n: any) => n.type === 'submission-form' || (n.data as any)?.isSubmissionForm
		);

		// Check if there are processing nodes (besides submission form)
		const hasProcessingNodes = nodes.some(
			(n: any) => !(n.type === 'submission-form' || (n.data as any)?.isSubmissionForm)
		);

		return hasProperTitle && hasSubmissionForm && hasProcessingNodes;
	};

	// Validate metadata fields according to requirements
	const validateMetadata = () => {
		const errors: string[] = [];

		// Validate name (required, not blank)
		if (!workflowMetadata.title || workflowMetadata.title.trim() === '') {
			errors.push('Workflow name is required and cannot be blank');
		}

		// Validate description (if provided, cannot be blank)
		if (workflowMetadata.description && workflowMetadata.description.trim() === '') {
			errors.push('Workflow description cannot be blank if provided');
		}

		// Validate tags (optional but if present, must be valid)
		if (workflowMetadata.tags && !Array.isArray(workflowMetadata.tags)) {
			errors.push('Tags must be an array');
		} else if (workflowMetadata.tags && workflowMetadata.tags.length > 0) {
			// Convert to strings for validation
			const tagStrings = workflowMetadata.tags.map(tag =>
				typeof tag === 'string' ? tag : (tag as any)?.name || ''
			);
			if (tagStrings.some(tag => typeof tag !== 'string' || tag.trim() === '')) {
				errors.push('All tags must be non-empty strings');
			}
		}

		// Validate folderId (optional but if present, must be valid UUID format)
		if (
			workflowMetadata.folderId &&
			!workflowMetadata.folderId.match(
				/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
			)
		) {
			errors.push('Folder ID must be a valid UUID format');
		}

		return errors;
	};

	// Function to show error dialog
	const showErrorDialog = (error: ApiError) => {
		setErrorDialog({
			isOpen: true,
			error,
		});
	};

	// Function to close error dialog
	const closeErrorDialog = () => {
		setErrorDialog({
			isOpen: false,
			error: null,
		});
	};

	// Function to show success dialog
	const showSuccessDialog = (
		workflowId: string,
		workflowName: string,
		isPublished: boolean = false
	) => {
		setSuccessDialog({
			isOpen: true,
			workflowId,
			workflowName,
			isPublished,
		});
	};

	// Function to close success dialog
	const closeSuccessDialog = () => {
		setSuccessDialog({
			isOpen: false,
			workflowId: '',
			workflowName: '',
			isPublished: false,
		});
	};

	// Function to handle status change from success dialog
	const handleStatusChange = (newStatus: 'published' | 'draft') => {
		// Update the success dialog state
		setSuccessDialog(prev => ({
			...prev,
			isPublished: newStatus === 'published',
		}));

		// Update the workflow metadata to reflect the new status
		setWorkflowMetadata(prev => ({
			...prev,
			status: newStatus,
		}));
	};

	// Toast helper functions
	const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
		setToast({
			isVisible: true,
			message,
			type,
		});
	};

	const hideToast = () => {
		setToast(prev => ({ ...prev, isVisible: false }));
	};

	// Function to trigger setup dialog for saving
	const handleSaveWorkflow = async () => {
		// If workflow is published, auto-save it and show toast
		if (workflowMetadata.status === 'published' && workflowMetadata.id) {
			try {
				setIsSavingWorkflow(true);

				// Get current workflow state
				const { nodes, edges } = getCurrentWorkflowState();

				// Clean up and validate workflow before sending to backend
				const validationWorkflow = {
					nodes,
					edges,
					metadata: workflowMetadata,
				};
				const { cleanedWorkflow } = cleanupWorkflowForSave(validationWorkflow);

				// Transform the cleaned workflow to API format
				const cleanedApiData = transformEditorToApiFormat({
					nodes: cleanedWorkflow.nodes,
					edges: cleanedWorkflow.edges,
					metadata: workflowMetadata,
				});

				// Update existing workflow
				await api.flows.updateFlow(workflowMetadata.id, cleanedApiData as any);

				// Invalidate folder cache to update flowCount
				queryClient.invalidateQueries({ queryKey: folderTreeKeys.all });
				queryClient.invalidateQueries({ queryKey: folderTreeKeys.tree() });

				// Show success toast
				showToast(`"${workflowMetadata.title}" saved successfully`);

				// Update the updatedAt timestamp
				setWorkflowMetadata(prev => ({
					...prev,
					updatedAt: new Date().toISOString(),
				}));
			} catch (error: any) {
				console.error('Failed to auto-save published workflow:', error);
				showToast('Failed to save workflow', 'error');
			} finally {
				setIsSavingWorkflow(false);
			}
		} else {
			// For draft workflows or new workflows, show setup dialog
			setShowSetupDialog(true);
		}
	};

	/**
	 * Transform visual workflow state to API format
	 */
	const transformEditorToApiFormat = (editorState: any) => {
		let submissionForm = null;
		const dagNodes: any[] = [];

		// Separate legacy submission forms from regular nodes
		// Note: start.form nodes should remain in the DAG, not be extracted as submissionForm
		editorState.nodes.forEach((visualNode: any) => {
			if (visualNode.type === 'submission-form') {
				// Only extract legacy submission-form nodes, not new start.* nodes
				submissionForm = {
					id: visualNode.id,
					type: 'embedded' as const,
					position: visualNode.position,
					config: {
						name: visualNode.data?.config?.name || 'Form',
						description: visualNode.data?.config?.description || '',
						fields:
							visualNode.data?.config?.fields || visualNode.data?.formFields || [],
						validation: {
							allowDuplicates: false,
							sessionTimeout: 60,
						},
						styling: {
							theme: 'professional',
							layout: 'vertical',
						},
						successMessage:
							'Thank you for your submission! We will process your request.',
						errorMessage: 'Please correct the errors below and resubmit.',
					},
				};
			} else {
				// Regular processing nodes for DAG
				let nodeConfig = {
					description: visualNode.data?.config?.description || '',
					...visualNode.data?.config,
				};

				// Special handling for condition nodes - convert complex config to simple expression
				if (visualNode.type === 'condition' && nodeConfig.match && nodeConfig.conditions) {
					const expression = convertConditionConfigToExpression(nodeConfig);
					nodeConfig = {
						description: nodeConfig.description || '',
						expression: expression,
					};
				}

				// Special handling for start.form nodes - ensure form fields are properly configured
				if (visualNode.type === 'start.form') {
					nodeConfig = {
						description: nodeConfig.description || '',
						...nodeConfig,
						// Ensure fields are available from either config.fields or formFields
						fields: nodeConfig.fields || visualNode.data?.formFields || [],
					};
				}

				dagNodes.push({
					id: visualNode.id,
					type: visualNode.type,
					label: visualNode.data?.label || visualNode.type,
					position: visualNode.position,
					config: nodeConfig,
				});
			}
		});

		// Don't create default submission form - allow workflows without forms
		// Users can choose their own trigger type (form, webhook, schedule, etc.)

		// Transform edges to match expected format
		const dagEdges = (editorState.edges || []).map((edge: any) => {
			// Find the source node to determine if it's a condition node
			const sourceNode = editorState.nodes.find((node: any) => node.id === edge.source);

			let condition = edge.data?.condition || 'success';

			// If the source is a condition node, set appropriate condition based on edge metadata
			if (sourceNode?.type === 'condition') {
				// Check if edge has explicit condition data
				if (edge.data?.condition) {
					condition = edge.data.condition;
				} else {
					// Try to determine condition from edge label
					const edgeLabel = edge.data?.label || edge.label || '';
					const lowerLabel = edgeLabel.toLowerCase();

					if (
						lowerLabel.includes('true') ||
						lowerLabel.includes('yes') ||
						lowerLabel.includes('success')
					) {
						condition = 'true';
					} else if (
						lowerLabel.includes('false') ||
						lowerLabel.includes('no') ||
						lowerLabel.includes('failure')
					) {
						condition = 'false';
					} else {
						// Fallback: use visual positioning to determine true/false
						// Get all outgoing edges from this condition node
						const outgoingEdges = editorState.edges.filter(
							(e: any) => e.source === edge.source
						);

						if (outgoingEdges.length === 1) {
							// Single edge - take it regardless of condition result
							condition = 'true';
						} else if (outgoingEdges.length === 2) {
							// Two edges - determine based on target node positions
							const targetNode = editorState.nodes.find(
								(n: any) => n.id === edge.target
							);
							const otherEdge = outgoingEdges.find((e: any) => e.id !== edge.id);
							const otherTargetNode = editorState.nodes.find(
								(n: any) => n.id === otherEdge?.target
							);

							if (targetNode && otherTargetNode) {
								// Compare Y positions - typically "true" path goes up/left, "false" goes down/right
								// But also consider X positions for horizontal layouts
								const thisY = targetNode.position?.y || 0;
								const otherY = otherTargetNode.position?.y || 0;
								const thisX = targetNode.position?.x || 0;
								const otherX = otherTargetNode.position?.x || 0;

								// If Y positions are significantly different, use Y for determination
								if (Math.abs(thisY - otherY) > 50) {
									condition = thisY < otherY ? 'true' : 'false';
								} else {
									// If Y positions are similar, use X position
									condition = thisX < otherX ? 'true' : 'false';
								}
							} else {
								// Fallback to array order as last resort
								const edgeIndex = outgoingEdges.findIndex(
									(e: any) => e.id === edge.id
								);
								condition = edgeIndex === 0 ? 'true' : 'false';
							}
						} else {
							// Multiple edges - default to "true" for backward compatibility
							condition = 'true';
						}
					}
				}
			}

			return {
				id: edge.id,
				source: edge.source,
				target: edge.target,
				condition: condition,
			};
		});

		return {
			name: editorState.metadata.title,
			description: editorState.metadata.description,
			folderId: editorState.metadata.folderId,
			tags: editorState.metadata.tags || [],
			submissionForm,
			dagDefinition: {
				nodes: dagNodes,
				edges: dagEdges,
				global: {
					timeout: 300000,
					retries: 2,
					variables: {
						workflow_type: 'custom',
						created_from: 'visual_editor',
					},
				},
			},
			config: {
				allowConcurrentExecutions: true,
				maxConcurrentExecutions: 10,
				executionTimeout: 600000,
				enableWebhooks: false,
				webhookUrls: [],
			},
		};
	};

	/**
	 * Validate current workflow using new validation functions
	 */
	const validateCurrentWorkflow = () => {
		const { nodes, edges } = getCurrentWorkflowState();

		const workflow = {
			submissionForm: nodes.find(
				(n: any) => n.type === 'submission-form' || (n.data as any)?.isSubmissionForm
			),
			nodes: nodes.filter(
				(n: any) => !(n.type === 'submission-form' || (n.data as any)?.isSubmissionForm)
			),
			edges: edges,
		};

		// Check for deprecated patterns
		const hasDeprecated = workflow.nodes.some((node: any) => node.type === 'start');
		if (hasDeprecated) {
			alert({
				title: 'Deprecated Nodes Found',
				message:
					'This workflow contains deprecated start nodes. Please remove them and connect the submission form directly to processing nodes.',
				type: 'warning',
			});
			return false;
		}

		// Check submission form exists
		if (!workflow.submissionForm) {
			alert({
				title: 'Missing Submission Form',
				message: 'Workflow must have a submission form trigger.',
				type: 'warning',
			});
			return false;
		}

		// Check submission form is connected
		const connectedFromForm = workflow.edges.some(
			(edge: any) => edge.source === workflow.submissionForm?.id
		);
		if (!connectedFromForm && workflow.nodes.length > 0) {
			alert({
				title: 'Disconnected Submission Form',
				message: 'Submission form must connect to at least one processing node.',
				type: 'warning',
			});
			return false;
		}

		return true;
	};

	// State to prevent duplicate saves
	const [isSavingWorkflow, setIsSavingWorkflow] = useState(false);

	// Updated workflow setup handler to handle save-triggered setup for both new and existing workflows
	const handleWorkflowSetup = async (
		title: string,
		description: string,
		folderId?: string,
		tags: any[] = [] // Accept both Tag[] and string[] for backward compatibility
	) => {
		// Prevent duplicate saves
		if (isSavingWorkflow) {
			console.log('Save already in progress, ignoring duplicate request');
			return;
		}

		setIsSavingWorkflow(true);

		const now = new Date().toISOString();
		const isNewWorkflow = !initialWorkflowData?.metadata?.id;

		try {
			// Convert Tag objects to strings for backward compatibility
			const tagsAsStrings = tags.map(tag => (typeof tag === 'string' ? tag : tag.name));

			// Update metadata state first
			const updatedMetadata = {
				id: initialWorkflowData?.metadata?.id, // Preserve existing ID for updates
				title,
				description,
				folderId,
				tags: tagsAsStrings,
				updatedAt: now,
				createdAt: initialWorkflowData?.metadata?.createdAt || now,
			};

			// Validate the updated metadata
			const errors: string[] = [];

			// Validate name (required, not blank)
			if (!title || title.trim() === '') {
				errors.push('Workflow name is required and cannot be blank');
			}

			// Validate description (if provided, cannot be blank)
			if (description && description.trim() === '') {
				errors.push('Workflow description cannot be blank if provided');
			}

			// Validate tags (optional but if present, must be valid after conversion)
			if (tagsAsStrings && !Array.isArray(tagsAsStrings)) {
				errors.push('Tags must be an array');
			} else if (
				tagsAsStrings &&
				tagsAsStrings.some(tag => typeof tag !== 'string' || tag.trim() === '')
			) {
				errors.push('All tags must be non-empty strings');
			}

			// Validate folderId (optional but if present, must be valid UUID format)
			if (
				folderId &&
				!folderId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)
			) {
				errors.push('Folder ID must be a valid UUID format');
			}

			// Show validation errors if any
			if (errors.length > 0) {
				showErrorDialog({
					message: errors,
					error: 'Validation Error',
					statusCode: 400,
				});
				return; // Don't close dialog, let user fix the issues
			}

			setWorkflowMetadata(updatedMetadata);

			// Mark setup as seen
			localStorage.setItem('workflow-setup-seen', 'true');

			// Always attempt to save after setup completion (even if empty)
			// Get current workflow state
			const { nodes, edges } = getCurrentWorkflowState();

			// Use the current workflow state as-is (no default nodes)
			const nodesToSave = nodes;

			// Clean up and validate workflow before sending to backend
			const validationWorkflow = {
				nodes: nodesToSave,
				edges,
				metadata: updatedMetadata,
			};
			const { cleanedWorkflow, validationResult } =
				cleanupWorkflowForSave(validationWorkflow);

			// Log warnings but don't block save
			if (validationResult.warnings.length > 0) {
				console.warn('Workflow validation warnings:', validationResult.warnings);
			}

			if (validationResult.errors.length > 0) {
				console.warn(
					'Workflow validation errors (continuing anyway):',
					validationResult.errors
				);
			}

			// Transform the cleaned workflow to API format
			const cleanedApiData = transformEditorToApiFormat({
				nodes: cleanedWorkflow.nodes,
				edges: cleanedWorkflow.edges,
				metadata: updatedMetadata,
			});

			let savedFlow;
			if (isNewWorkflow) {
				// Create new workflow with API-formatted data
				savedFlow = await api.flows.createFlow(cleanedApiData as any);

				// Invalidate folder cache to update flowCount after workflow creation
				queryClient.invalidateQueries({ queryKey: folderTreeKeys.all });
				queryClient.invalidateQueries({ queryKey: folderTreeKeys.tree() });

				navigate(`/workflows/${savedFlow.id}/edit`, { replace: true }); // updates the URL without pushing to history stack
			} else {
				// Update existing workflow with API-formatted data
				savedFlow = await api.flows.updateFlow(updatedMetadata.id!, cleanedApiData as any);
				console.log('Workflow updated successfully:', savedFlow);
			}

			// Invalidate folder cache to update flowCount after workflow changes
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.all });
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.tree() });

			// Update local metadata with the latest status from the API
			// If savedFlow.status is undefined, preserve the current status (API might not return it on updates)
			const finalStatus = savedFlow.status || workflowMetadata.status;
			setWorkflowMetadata(prev => ({
				...prev,
				status: finalStatus,
				updatedAt: savedFlow.updatedAt || new Date().toISOString(),
			}));

			// Always show success dialog with correct published status
			// This allows users to change publish status or navigate after saving
			console.log('DEBUG: Showing success dialog with status:', {
				savedFlowStatus: savedFlow.status,
				currentMetadataStatus: workflowMetadata.status,
				finalStatus: finalStatus,
				isPublished: finalStatus === 'published',
			});
			showSuccessDialog(savedFlow.id, title, finalStatus === 'published');
		} catch (error: any) {
			console.error(`Failed to ${isNewWorkflow ? 'create' : 'update'} workflow:`, error);

			// Show error dialog with API error details
			if (error.message || error.statusCode) {
				showErrorDialog({
					message: error.message || 'An unexpected error occurred',
					error: error.error || 'Error',
					statusCode: error.statusCode || 500,
				});
			} else {
				showErrorDialog({
					message: `Failed to ${
						isNewWorkflow ? 'save' : 'update'
					} workflow. Please check your connection and try again.`,
					error: 'Network Error',
					statusCode: 500,
				});
			}
		} finally {
			setIsSavingWorkflow(false);
		}
	};

	const handleCloseSetup = () => {
		setShowSetupDialog(false);
		// Mark setup as seen even if skipped
		localStorage.setItem('workflow-setup-seen', 'true');
	};

	// Auto-save functionality with debouncing
	const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const isAutoSavingRef = useRef<boolean>(false);

	const autoSaveWorkflow = useCallback(async () => {
		// Skip auto-save if workflow is loading, already saving, or auto-save is in progress
		if (isLoading || isSavingWorkflow || isAutoSavingRef.current) {
			console.log('Skipping auto-save: Workflow is loading or save already in progress');
			return;
		}

		isAutoSavingRef.current = true;

		const { nodes, edges } = getCurrentWorkflowState();

		try {
			// Only auto-save existing workflows (new workflows should only be saved via setup dialog)
			const workflowId = initialWorkflowData?.metadata?.id;

			if (!workflowId) {
				console.log(
					'Skipping auto-save: New workflows should only be saved via setup dialog'
				);
				return;
			}

			console.log('Auto-saving existing workflow...');

			// Safety check: Don't auto-save if we're in the middle of loading an existing workflow
			if (initialWorkflowData && nodes.length === 0) {
				console.log('Skipping auto-save: Existing workflow is still loading');
				return;
			}

			// Clean up workflow data (but don't require complete validation)
			const validationWorkflow = {
				nodes,
				edges,
				metadata: workflowMetadata,
			};
			const { cleanedWorkflow, validationResult } =
				cleanupWorkflowForSave(validationWorkflow);

			// Log warnings but don't block save
			if (validationResult.warnings.length > 0) {
				console.warn('Auto-save warnings (continuing anyway):', validationResult.warnings);
			}

			if (validationResult.errors.length > 0) {
				console.warn(
					'Auto-save validation errors (continuing anyway):',
					validationResult.errors
				);
			}

			// Transform the cleaned workflow to API format
			const cleanedApiData = transformEditorToApiFormat({
				nodes: cleanedWorkflow.nodes,
				edges: cleanedWorkflow.edges,
				metadata: workflowMetadata,
			});

			// Update existing workflow
			await api.flows.updateFlow(workflowId, cleanedApiData as any);

			// Invalidate folder cache to update flowCount after auto-save
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.all });
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.tree() });

			console.log('Existing workflow auto-saved successfully');

			// Update the updatedAt timestamp
			setWorkflowMetadata(prev => ({
				...prev,
				updatedAt: new Date().toISOString(),
			}));
		} catch (error: any) {
			console.error('Auto-save failed:', error);
			// Don't show error dialogs for auto-save failures to avoid interrupting user
		} finally {
			isAutoSavingRef.current = false;
		}
	}, [
		workflowMetadata,
		initialWorkflowData,
		getCurrentWorkflowState,
		cleanupWorkflowForSave,
		transformEditorToApiFormat,
		reactFlowInstance,
		isLoading,
		isSavingWorkflow,
	]);

	// Debounced auto-save function
	const debouncedAutoSave = useCallback(() => {
		// Clear existing timeout
		if (autoSaveTimeoutRef.current) {
			clearTimeout(autoSaveTimeoutRef.current);
		}

		// Set new timeout for auto-save (1 second delay for more responsive saving)
		autoSaveTimeoutRef.current = setTimeout(() => {
			autoSaveWorkflow();
		}, 1000);
	}, [autoSaveWorkflow]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (autoSaveTimeoutRef.current) {
				clearTimeout(autoSaveTimeoutRef.current);
			}
		};
	}, []);

	const handleResetWorkflow = () => {
		confirm({
			title: 'Reset Workflow',
			message:
				'Are you sure you want to reset the workflow? This will clear all data and show the setup dialog again.',
			confirmText: 'Reset',
			cancelText: 'Cancel',
			type: 'danger',
			onConfirm: () => {
				// Reset workflow metadata
				setWorkflowMetadata({
					title: 'Untitled Workflow',
					description: '',
					folderId: undefined,
					tags: [],
					createdAt: new Date().toISOString(),
					updatedAt: new Date().toISOString(),
					status: 'draft',
				});

				// Clear all nodes and edges
				if (reactFlowInstance) {
					reactFlowInstance.setNodes([]);
					reactFlowInstance.setEdges([]);
				}

				// Show setup dialog again
				localStorage.removeItem('workflow-setup-seen');
				setShowSetupDialog(true);
			},
		});
	};

	const extendedWorkflowState = {
		...workflowState,
		getAvailableFields: (nodeId: string) =>
			getAvailableFields(
				nodeId,
				getCurrentWorkflowState().nodes,
				getCurrentWorkflowState().edges
			),
		getFlattenedFields: (nodeId: string) =>
			getFlattenedFields(
				nodeId,
				getCurrentWorkflowState().nodes,
				getCurrentWorkflowState().edges
			),
		getCurrentFlowId: () => {
			// Try to get flow ID from various sources
			const urlWorkflowId = window.location.pathname.split('/').pop();
			const urlParams = new URLSearchParams(window.location.search);
			const paramWorkflowId = urlParams.get('workflowId');

			// Return the first valid UUID found
			const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

			if (urlWorkflowId && uuidRegex.test(urlWorkflowId)) {
				return urlWorkflowId;
			}

			if (paramWorkflowId && uuidRegex.test(paramWorkflowId)) {
				return paramWorkflowId;
			}

			// Return null if no valid flow ID found
			return null;
		},
	};

	return (
		<MenuProvider>
			<WorkflowProvider value={extendedWorkflowState}>
				<div className="flex h-screen">
					<Sidebar />
					<div className="flex flex-col flex-1">
						<Header
							workflowMetadata={workflowMetadata}
							onEditWorkflow={() => setShowSetupDialog(true)}
							onResetWorkflow={handleResetWorkflow}
							onSaveWorkflow={handleSaveWorkflow}
						/>
						<div className="flex-1">
							<WorkflowEditor
								onReactFlowInit={setReactFlowInstance}
								onNodeChange={debouncedAutoSave}
								onEdgeChange={debouncedAutoSave}
							/>
						</div>
					</div>
				</div>

				<WorkflowSetupDialog
					isOpen={showSetupDialog}
					onClose={handleCloseSetup}
					onSave={handleWorkflowSetup}
					initialTitle={
						workflowMetadata.title === 'Untitled Workflow' ? '' : workflowMetadata.title
					}
					initialDescription={workflowMetadata.description}
					initialFolderId={workflowMetadata.folderId}
					initialTags={workflowMetadata.tags}
					isSaving={isSavingWorkflow}
				/>

				<ErrorDialog
					isOpen={errorDialog.isOpen}
					onClose={closeErrorDialog}
					error={errorDialog.error}
					title="Error Saving Workflow"
				/>

				<WorkflowSuccessDialog
					isOpen={successDialog.isOpen}
					onClose={closeSuccessDialog}
					workflowId={successDialog.workflowId}
					workflowName={successDialog.workflowName}
					isPublished={successDialog.isPublished}
					onStatusChange={handleStatusChange}
				/>

				{/* Toast Notification */}
				{toast.isVisible && (
					<Toast message={toast.message} type={toast.type} onClose={hideToast} />
				)}
			</WorkflowProvider>
		</MenuProvider>
	);
}

/**
 * Main layout component for most pages
 */
function MainLayout({ children }: { children: React.ReactNode }) {
	return (
		<div className="min-h-screen bg-gray-50 transition-colors dark:bg-gray-900">
			<NavigationBar />
			<main className="py-8">{children}</main>
		</div>
	);
}

export default function App() {
	// Production performance monitoring (silent background monitoring)
	useProductionPerformanceMonitor({
		enableErrorReporting: true,
		enableMetrics: true,
		criticalMemoryThreshold: 250, // 250MB threshold for production
		enableAutoCleanup: true,
	});

	// Initialize enterprise optimizations
	React.useEffect(() => {
		initializeEnterprise();
	}, []);

	console.info(
		`${import.meta.env.VITE_ENV} App Is Running with api endpoint: ${
			import.meta.env.VITE_API_URL
		}`
	);
	return (
		<QueryClientProvider client={queryClient}>
			<Router>
				<ErrorBoundary
					showDetails={import.meta.env.DEV}
					enableAutoRetry={true}
					retryDelay={3000}
				>
					<AuthProvider>
						<ReactFlowProvider>
							<ThemeProvider>
								<DialogProvider>
									<ClipboardProvider>
										<Routes>
											{/* Public login page */}
											<Route path="/login" element={<LoginPage />} />

											{/* Form submission page - dynamic layout based on auth */}
											<Route
												path="/flows/:flowId/form"
												element={
													<FormLayout>
														<TakeFormPage />
													</FormLayout>
												}
											/>

											{/* Default redirect to workflows list */}
											<Route
												path="/"
												element={<Navigate to="/flows" replace />}
											/>

											{/* Protected workflow management pages */}
											<Route
												path="/flows"
												element={
													<ProtectedRoute>
														<MainLayout>
															<WorkflowList />
														</MainLayout>
													</ProtectedRoute>
												}
											/>

											{/* Legacy folder manager */}
											<Route
												path="/folders/manage"
												element={
													<ProtectedRoute>
														<MainLayout>
															<FolderManager />
														</MainLayout>
													</ProtectedRoute>
												}
											/>

											{/* Nested folders view - new default */}
											<Route
												path="/folders/:folderId"
												element={
													<ProtectedRoute>
														<MainLayout>
															<FoldersPage />
														</MainLayout>
													</ProtectedRoute>
												}
											/>

											{/* Root folders view */}
											<Route
												path="/folders"
												element={
													<ProtectedRoute>
														<MainLayout>
															<FoldersPage />
														</MainLayout>
													</ProtectedRoute>
												}
											/>

											<Route
												path="/tags"
												element={
													<ProtectedRoute
														requiredRoles={['WRITE', 'ADMIN']}
													>
														<MainLayout>
															<TagsAdmin />
														</MainLayout>
													</ProtectedRoute>
												}
											/>

											{/* Protected execution management pages */}
											<Route
												path="/executions"
												element={
													<ProtectedRoute>
														<MainLayout>
															<ExecutionList />
														</MainLayout>
													</ProtectedRoute>
												}
											/>

											<Route
												path="/executions/:id"
												element={
													<ProtectedRoute>
														<MainLayout>
															<ExecutionDetail />
														</MainLayout>
													</ProtectedRoute>
												}
											/>

											{/* Protected workflow editor */}
											<Route
												path="/editor"
												element={
													<ProtectedRoute
														requiredRoles={['WRITE', 'ADMIN']}
													>
														<WorkflowApp />
													</ProtectedRoute>
												}
											/>

											{/* Admin user management */}
											<Route
												path="/admin/users"
												element={
													<ProtectedRoute requiredRoles={['ADMIN']}>
														<MainLayout>
															<AdminUsersPage />
														</MainLayout>
													</ProtectedRoute>
												}
											/>

											{/* Security dashboard */}
											<Route
												path="/admin/security"
												element={
													<ProtectedRoute requiredRoles={['ADMIN']}>
														<MainLayout>
															<SecurityDashboard />
														</MainLayout>
													</ProtectedRoute>
												}
											/>

											{/* Audit logs */}
											<Route
												path="/admin/audit"
												element={
													<ProtectedRoute requiredRoles={['ADMIN']}>
														<MainLayout>
															<AuditLogsPage />
														</MainLayout>
													</ProtectedRoute>
												}
											/>

											{/* Protected edit existing workflow */}
											<Route
												path="/workflows/:workflowId/edit"
												element={
													<ProtectedRoute
														requiredRoles={['WRITE', 'ADMIN']}
													>
														<EditWorkflowPage />
													</ProtectedRoute>
												}
											/>

											{/* Catch all route - redirect to flows */}
											<Route
												path="*"
												element={<Navigate to="/flows" replace />}
											/>
										</Routes>
									</ClipboardProvider>
								</DialogProvider>
							</ThemeProvider>
						</ReactFlowProvider>
					</AuthProvider>
				</ErrorBoundary>
			</Router>

			{/* Simple Performance Monitoring - safe from infinite loops */}
			{import.meta.env.DEV && <SimplePerformanceIndicator position="bottom-right" />}

			{/* React Query Devtools - only in development */}
			{import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
		</QueryClientProvider>
	);
}
